/**
 * How far a scan opened page by page has got, over the score it has so far.
 *
 * One chip a page: filled where the page is read and laid out below, dashed where it is still
 * being read. The pages are laid on in order, from the first, so a page read before one ahead
 * of it is still dashed until that one is in. The sentence says it as a count, which is what a
 * screen reader hears; the chips are the same thing drawn.
 */

import type { ScanReady } from "../lib/arrange-open";

/** How many of the scan's pages are read and laid out. */
export function pagesRead(ready: ScanReady): number {
  return ready.pages - ready.waiting.length;
}

/** "K of N pages ready", as the strip and the line over Stop say it. */
export function pagesReady(ready: ScanReady): string {
  return `${pagesRead(ready)} of ${ready.pages} page${ready.pages === 1 ? "" : "s"} ready`;
}

export default function ScanPagesReady({ ready }: { readonly ready: ScanReady }): JSX.Element {
  const waiting = new Set(ready.waiting);
  const pages = [...Array(ready.pages).keys()].map((at) => at + 1);
  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-shell-edge bg-shell-bar px-4 py-2.5"
    >
      <div aria-hidden="true" className="flex flex-wrap gap-1">
        {pages.map((page) => (
          <span
            key={page}
            className={`inline-flex h-6 w-6 items-center justify-center rounded font-mono text-[11px] ${
              waiting.has(page)
                ? "border border-dashed border-shell-seam text-shell-text"
                : "bg-shell-live text-white"
            }`}
          >
            {page}
          </span>
        ))}
      </div>
      <span className="min-w-[16rem] flex-1 text-[14px] text-shell-lit">
        {pagesReady(ready)}. Play them now; the rest are laid on in order as they are read.
      </span>
    </div>
  );
}
